import type {GenType, ScreenSize} from '@features/flow/flowService';

/** Modern genTypes that support HTML/CSS output */
type ModernGenType = 'web-html-css' | 'mobile-html-css';

/** Type guard to check if a genType is a modern format */
export function isModernGenType(
  genType: GenType | null | undefined,
): genType is ModernGenType {
  return genType === 'web-html-css' || genType === 'mobile-html-css';
}

/** Normalize legacy genTypes to modern format */
export function normalizeGenType(
  genType: GenType | ScreenSize | null | undefined,
): GenType {
  if (genType === 'desktop') return 'web-html-css';
  if (!genType) return 'web-html-css';
  if (genType === 'mobile') return 'mobile-html-css';
  if (genType === 'web') return 'web-html-css';
  return genType;
}

/**
 * Returns the effective genType for a structure with fallback logic.
 *
 * Resolution priority:
 * 1. structure.genType (per-structure override for mixed-platform projects)
 * 2. streamingGenType (interim state during generation, before structure exists)
 * 3. flow.genType (project default)
 * 4. 'web-html-css' (ultimate fallback for legacy data)
 *
 * This ensures backwards compatibility with existing projects that only have
 * genType stored at the flow level, while supporting mixed-platform projects
 * and correct rendering during streaming.
 */
export function getEffectiveGenType(
  structureScreenSize: ScreenSize | GenType | null | undefined,
  streamingGenType: GenType | null | undefined,
  flowGenType: GenType | null | undefined,
): GenType {
  // Priority: structure > streaming > flow > default
  if (structureScreenSize) {
    return normalizeGenType(structureScreenSize);
  }

  if (streamingGenType) {
    return normalizeGenType(streamingGenType);
  }

  if (flowGenType) {
    return normalizeGenType(flowGenType);
  }

  return 'web-html-css';
}
